import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { UserService } from './user.service';

export interface PostLoginRequest {
  empId: string;
  password: string;
}

export interface PostLoginResponse {
  accessToken: string;
  refreshToken: string;
  refreshTokenExpiredAt: string;
  expiresIn: string; // UTC 時間字串，代表 Access Token 的過期時間
  user: {
    uuid: string;
    empId: string;
    name: string;
    departmentId: number;
    roleId: number;
  };
}

export interface PostAuthRefreshTokenRequest {
  accessToken: string;
  refreshToken: string;
}

export interface PostAuthRefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  refreshTokenExpiredAt: string;
  expiresIn: string; // UTC 時間字串，代表 Access Token 的過期時間
  user: {
    uuid: string;
    empId: string;
    name: string;
    departmentId: number;
    roleId: number;
  };
}

export interface GetAuthProfileResponse {
  data: {
    uuid: string;
    userName: string;
    empId: string;
    name: string;
    email: string;
    departmentName: string;
    role: {
      roleId: number;
      roleName: string;
      permissions: {
        functionName: string;
        canView: boolean;
        canCreate: boolean;
        canEdit: boolean;
        canDelete: boolean;
        canViewReport: boolean;
        canDownload: boolean;
      }[];
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticateService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private userService = inject(UserService);
  private readonly baseUrl = environment.apiUrl + '/api/Auth';

  public accessToken = signal<string | null>(localStorage.getItem('access_token'));

  public logoutCountdown = signal<number>(1200); // 預設 20 分鐘 = 1200 秒
  public logoutCountdownFormatted = computed(() => {
    const totalSeconds = this.logoutCountdown();
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });

  private logoutInterval: ReturnType<typeof setInterval> | null = null;
  private readonly AUTO_LOGOUT_SECONDS = 20 * 60; // 1200 秒
  private clickListener: (() => void) | null = null;

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (localStorage.getItem('access_token')) {
      this.scheduleAutoLogout();

      const expiredAt = localStorage.getItem('access_token_expired_at');
      if (expiredAt) {
        this.scheduleTokenRefresh(expiredAt);
      }
    }
  }

  scheduleAutoLogout(): void {
    this.clearLogoutInterval();
    this.logoutCountdown.set(this.AUTO_LOGOUT_SECONDS);

    this.logoutInterval = setInterval(() => {
      const current = this.logoutCountdown();
      if (current <= 1) {
        this.forceLogout();
      } else {
        this.logoutCountdown.set(current - 1);
      }
    }, 1000);

    if (!this.clickListener) {
      this.clickListener = () => this.scheduleAutoLogout();
      document.addEventListener('click', this.clickListener);
    }
  }

  private clearLogoutInterval(): void {
    if (this.logoutInterval !== null) {
      clearInterval(this.logoutInterval);
      this.logoutInterval = null;
    }
  }

  private removeClickListener(): void {
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
      this.clickListener = null;
    }
  }

  private forceLogout(): void {
    this.clearLogoutInterval();
    this.clearRefreshTimer();
    this.removeClickListener();
    this.logoutCountdown.set(this.AUTO_LOGOUT_SECONDS);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('access_token_expired_at');
    this.accessToken.set(null);
    this.userService.clearProfile();
    this.router.navigate(['/login']);
  }

  scheduleTokenRefresh(expiresIn: string): void {
    this.clearRefreshTimer();

    const expireTime = new Date(expiresIn).getTime();
    const timeRemaining = expireTime - Date.now();

    // 預設提前 1 分鐘刷新；若時間太短，則在 80% 時限處刷新
    let refreshDelay = timeRemaining - 60 * 1000;
    if (refreshDelay <= 0) {
      refreshDelay = timeRemaining * 0.8;
    }

    if (refreshDelay > 0) {
      this.refreshTimer = setTimeout(() => {
        const accessToken = localStorage.getItem('access_token') || '';
        const refreshToken = localStorage.getItem('refresh_token') || '';
        if (accessToken && refreshToken) {
          this.apiPostRefreshToken({ accessToken, refreshToken }).subscribe({
            error: (err) => console.error('[Auth] Failed to auto-refresh token:', err)
          });
        }
      }, refreshDelay);
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  apiPostLogin(request: PostLoginRequest) {
    return this.http.post<PostLoginResponse>(`${this.baseUrl}/Login`, request).pipe(
      tap((response) => {
        localStorage.setItem('access_token', response.accessToken);
        localStorage.setItem('refresh_token', response.refreshToken);
        localStorage.setItem('access_token_expired_at', response.expiresIn);
        this.accessToken.set(response.accessToken);
        this.scheduleAutoLogout();
        this.scheduleTokenRefresh(response.expiresIn);
      })
    );
  }

  apiGetAuthProfile() {
    return this.http.get<GetAuthProfileResponse>(`${this.baseUrl}/Profile`);
  }

  apiPostRefreshToken(request: PostAuthRefreshTokenRequest) {
    return this.http.post<PostAuthRefreshTokenResponse>(`${this.baseUrl}/RefreshToken`, request).pipe(
      tap((response) => {
        localStorage.setItem('access_token', response.accessToken);
        localStorage.setItem('refresh_token', response.refreshToken);
        localStorage.setItem('access_token_expired_at', response.expiresIn);
        this.accessToken.set(response.accessToken);
        this.scheduleTokenRefresh(response.expiresIn);
      })
    );
  }

  apiPostAuthLogout() {
    return this.http.post<any>(`${this.baseUrl}/Logout`, {}).pipe(
      tap(() => {
        this.clearLogoutInterval();
        this.clearRefreshTimer();
        this.removeClickListener();
        this.logoutCountdown.set(this.AUTO_LOGOUT_SECONDS);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token_expired_at');
        this.accessToken.set(null);
        this.userService.clearProfile();
        this.router.navigate(['/login']);
      })
    );
  }

}
